import React, { useState, useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { UserOutlined, LockOutlined, GoogleOutlined, AppleFilled } from "@ant-design/icons";
import { Capacitor } from "@capacitor/core";
import BackButton from "../../../components/BackButton/BackButton";
import "./Login.scss";
import { auth, signInWithEmailAndPassword } from "../../../firebaseConfig";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useUser } from "../../../context/UserContext";
import { signInWithSocial } from "../../../utils/authProviders";
import { createCustomer } from "../../../Api/Openpay/OpenPayApi"; // 🔥 Ya la tenías importada
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import logo from "../../../assets/logo.png";

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser, userData } = useUser();

  useEffect(() => {
    // console.log('userData after setUser:', userData);
  }, [userData]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      );

      // Obtener una instancia de Firestore
      const db = getFirestore();
      const userId = userCredential.user.uid;

      // Obtener el documento de roles del usuario
      const userDoc = await getDoc(doc(db, "roles", userId));
      if (!userDoc.exists()) {
        // Existe en Firebase Auth pero no tiene perfil: no se puede continuar
        await auth.signOut();
        setLoading(false);
        toast.error("Tu cuenta no está completa. Contacta a soporte (ayuda@blizu.com.mx) o regístrate de nuevo.");
        return;
      }
      const userData = userDoc.data();

      if (!userData.activado && userData.role !== 1) {
        setLoading(false);
        toast.error(
          "Tu cuenta se encuentra deshabilitada. Favor de contactar a un administrador"
        );
        return;
      }

      setUser(userData);
      setLoading(false);

      if (userData && userData.role === 1) {
        if (userData.activado) {
          const tiendasCollection = collection(db, "tiendas");
          const tiendaDocRef = doc(tiendasCollection, userId);
          const tiendaDocSnapshot = await getDoc(tiendaDocRef);

          if (tiendaDocSnapshot.exists()) {
            navigate("/dashboard");
            toast.success("Inicio de sesión exitoso");
          } else {
            navigate("/creartienda");
            toast.success("Inicio de sesión exitoso. ¡Crea tu tienda ahora!");
          }
        } else {
          navigate("/");
          toast.success("¡Bienvenido! Tu solicitud de vendedor está en revisión, pero ya puedes comprar en Blizu.");
        }
      } else if (userData && userData.role === 2) {
        message.success("Inicio de sesión exitoso");
        navigate("/admin");
      } else {
        message.success("Inicio de sesión exitoso");
        navigate("/");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Credenciales incorrectas. Por favor, inténtalo de nuevo.");
      console.error(error.message);
    }
  };

  const showApple = Capacitor.getPlatform() === "ios";

  const handleGoogleLogin = () => handleSocialLogin("google");
  const handleAppleLogin = () => handleSocialLogin("apple");

  const handleSocialLogin = async (provider) => {
    try {
      setLoading(true);
      const user = await signInWithSocial(provider);
      const userId = user.uid;

      const db = getFirestore();
      const userDocRef = doc(db, "roles", userId);
      const snap = await getDoc(userDocRef);

      if (snap.exists()) {
        // --- USUARIO YA EXISTE ---
        const userData = snap.data();

        if (!userData.activado && userData.role !== 1) {
          toast.error("Tu cuenta está deshabilitada");
          return;
        }
        
        // (Opcional) Si un usuario viejo no tiene OpenpayId, podrías crearlo aquí también.
        
        setUser(userData);
        navigate("/");
        toast.success("Inicio de sesión exitoso");
      } else {
        // --- USUARIO NUEVO (REGISTRO) ---
        
        // 1. 🔥 CREAR CLIENTE EN OPENPAY PRIMERO
        let openpayId = "";
        try {
            const customerData = {
                name: user.displayName || user.email?.split("@")[0] || "Usuario",
                email: user.email,
                requires_account: false
            };
            const responseOpenpay = await createCustomer(customerData);
            openpayId = responseOpenpay.id; // Obtenemos el ID generado
            console.log("✅ Cliente Openpay creado:", openpayId);
        } catch (opError) {
            console.error("❌ Error creando cliente en Openpay:", opError);
            // Puedes decidir si detener el registro o continuar sin openpayId
        }

        // 2. GUARDAR EN FIREBASE CON EL ID DE OPENPAY
        const newUserData = {
          username: user.displayName || user.email?.split("@")[0] || "Usuario",
          email: user.email || "",
          role: 0,
          photo: user.photoURL || null,
          uid: userId,
          activado: true,
          google: provider === "google",
          apple: provider === "apple",
          openpayId: openpayId, // 🔥 ¡Aquí guardamos el ID importante!
        };

        await setDoc(userDocRef, newUserData);

        setUser(newUserData);
        toast.success("Registro exitoso");
        navigate("/");
      }

    } catch (error) {
      console.error("ERROR GOOGLE LOGIN:", error);
      if (error.code === 'firestore/invalid-argument') {
        toast.error("Error al guardar datos del usuario");
      } else {
        toast.error(error?.message || "Error al iniciar sesión");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <Toaster richColors position="top-center" />

      {/* SECCIÓN IZQUIERDA */}
      <div className="login-branding">
        <div className="brand-content">
          <img src={logo} alt="Blizu" className="brand-logo" /> 
          <h1>Bienvenido a Blizu</h1>
          <p>
            Tu marketplace de confianza. Encuentra productos y servicios locales 
            de forma rápida, segura y sin complicaciones.
          </p>
        </div>
      </div>

      {/* SECCIÓN DERECHA: FORMULARIO */}
      <div className="login-form-container">
        <div className="login-card-content">
          <BackButton onClick={() => navigate("/")} />
          
          <div className="login-header">
            <img src={logo} alt="Blizu" className="mobile-logo" />
            <h2>Iniciar sesión</h2>
            <span>Ingresa tus credenciales para continuar</span>
          </div>

          <Form
            name="basic"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="email"
              label={<span style={{ fontWeight: 600 }}>Correo electrónico</span>}
              rules={[{ required: true, message: "Ingresa tu correo" }]}
            >
              <Input prefix={<UserOutlined style={{ color: '#1dbf73' }} />} placeholder="ejemplo@blizu.com" />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span style={{ fontWeight: 600 }}>Contraseña</span>}
              rules={[{ required: true, message: "Ingresa tu contraseña" }]}
            >
              <Input.Password prefix={<LockOutlined style={{ color: '#1dbf73' }} />} placeholder="••••••••" />
            </Form.Item>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
               <Link to="/olvide" className="forgot-password-link">
                 ¿Olvidaste tu contraseña?
               </Link>
            </div>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block className="login-form-button">
                Ingresar
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center", margin: "20px 0", color: "#94a3b8", fontSize: '0.85rem' }}>
              o continúa con
            </div>

            <Form.Item>
              <Button
                icon={<GoogleOutlined style={{ color: '#ea4335' }} />}
                onClick={handleGoogleLogin}
                block
                className="google-btn"
              >
                Google
              </Button>
            </Form.Item>

            {showApple && (
              <Form.Item>
                <Button
                  icon={<AppleFilled />}
                  onClick={handleAppleLogin}
                  block
                  className="google-btn"
                >
                  Apple
                </Button>
              </Form.Item>
            )}

            <div className="signup-footer">
              <span>¿Aún no tienes cuenta? </span>
              <Link to="/tipo">
                Regístrate gratis
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;